import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Badge
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  PhoneCallback as PhoneCallbackIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { exportNextStepsToCSV } from '../utils/csvExport';
import { exportNextStepsToExcel } from '../utils/excelExport';

interface ActionItem {
  call_id: string;
  customer_name: string;
  customer_phone: string;
  call_date: string;
  duration: number;
  issue_category: string;
  issue_details: string;
  action_required: string;
  action_type: string;
  priority: string;
  status: string;
  next_steps: string[];
  key_quotes: string[];
  callback_requested: boolean;
  callback_status: string;
  urgency_level: string;
  ai_confidence: number;
  assigned_to: string;
  due_date: string;
  human_transfer_requested: boolean;
  escalation_requested: boolean;
  under_30_seconds: boolean;
  resolved: boolean;
  created_at: string;
  channel_type: string;
  troubleshooting_steps: string;
  priorityScore?: number; // Smart priority score
}

interface ReportSummary {
  totalActions: number;
  pendingActions: number;
  completedActions: number;
  callbackRequests: number;
  highPriorityActions: number;
  humanTransferRequests: number;
  escalationRequests: number;
  under30Seconds: number;
}

interface ReportData {
  summary: ReportSummary;
  actions: ActionItem[];
}

const NextStepsReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending'); // Default to pending only
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('7d');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [closeComment, setCloseComment] = useState('');
  const [ticketStatus, setTicketStatus] = useState('callback_done');
  const [closingTicket, setClosingTicket] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<Date>(new Date());
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [overdueCallbacks, setOverdueCallbacks] = useState<ActionItem[]>([]);

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        period: filterPeriod,
        priority: filterPriority,
        status: filterStatus,
        category: filterCategory
      });

      const response = await fetch(`/api/next-steps-report?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch report data');
      }

      const data = await response.json();
      setReportData(data);
      setLastFetchTime(new Date());
      
      // Calculate overdue callbacks
      const now = new Date();
      const overdue = data.actions.filter((action: ActionItem) => {
        const createdAt = new Date(action.created_at);
        const hoursSince = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
        return !action.resolved && hoursSince > 24;
      });
      setOverdueCallbacks(overdue);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterPeriod, filterPriority, filterStatus, filterCategory]);

  // Smart Priority Score Calculator
  const calculatePriorityScore = (action: ActionItem): number => {
    let score = 0;
    
    // Age factor (older = higher priority)
    const hoursSinceCall = (Date.now() - new Date(action.created_at).getTime()) / (1000 * 60 * 60);
    if (hoursSinceCall > 72) score += 40; // 3+ days
    else if (hoursSinceCall > 48) score += 30; // 2+ days
    else if (hoursSinceCall > 24) score += 20; // 1+ day
    else if (hoursSinceCall > 12) score += 10; // 12+ hours
    
    // Urgency factor
    if (action.urgency_level === 'Critical') score += 40;
    else if (action.urgency_level === 'High') score += 30;
    else if (action.urgency_level === 'Medium') score += 15;
    
    // Issue type factor
    if (action.issue_category === 'Billing') score += 20; // Money = important
    else if (action.issue_category === 'Internet') score += 15;
    else if (action.issue_category === 'Phone') score += 10;
    
    // Callback requested
    if (action.callback_requested) score += 25;
    
    // Escalation/Transfer flags
    if (action.escalation_requested) score += 35;
    if (action.human_transfer_requested) score += 30;
    
    // Under 30 seconds (likely disconnect)
    if (action.under_30_seconds) score += 25;
    
    return Math.min(score, 100); // Cap at 100
  };

  // Format time ago
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Get priority score color
  const getPriorityScoreColor = (score: number): 'error' | 'warning' | 'info' | 'success' => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  // Get row background color based on priority score
  const getRowBackgroundColor = (score: number): string => {
    if (score >= 80) return '#ffebee'; // Light red
    if (score >= 60) return '#fff3e0'; // Light orange
    if (score >= 40) return '#e3f2fd'; // Light blue
    return 'inherit';
  };

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger if Cmd (Mac) or Ctrl (Windows/Linux) is pressed
      if (e.metaKey || e.ctrlKey) {
        switch(e.key.toLowerCase()) {
          case 'k': // Cmd/Ctrl + K = Focus search
            e.preventDefault();
            const searchInput = document.querySelector('input[placeholder="Search actions..."]') as HTMLInputElement;
            searchInput?.focus();
            break;
          case 'r': // Cmd/Ctrl + R = Refresh
            e.preventDefault();
            fetchReportData();
            break;
          case 'n': // Cmd/Ctrl + N = View completed
            e.preventDefault();
            navigate('/completed-callbacks');
            break;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [fetchReportData, navigate]);

  const handleOpenCloseDialog = (callId: string) => {
    setSelectedCallId(callId);
    setTicketStatus('callback_done'); // Default status
    setCloseDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCloseDialogOpen(false);
    setSelectedCallId(null);
    setCloseComment('');
    setTicketStatus('callback_done');
  };

  const handleCloseTicket = async () => {
    if (!selectedCallId || !closeComment.trim() || !user?.email) {
      return;
    }

    try {
      setClosingTicket(true);
      
      // Determine the new callback_status based on ticket status
      let callbackStatus = 'pending'; // Default
      let shouldResolve = false;
      
      switch (ticketStatus) {
        case 'callback_done':
          callbackStatus = 'completed';
          shouldResolve = true;
          break;
        case 'email_sent':
          callbackStatus = 'completed';
          shouldResolve = true;
          break;
        case 'needs_followup':
          callbackStatus = 'needs_followup';
          shouldResolve = false; // Stays in Next Steps
          break;
        case 'escalated':
          callbackStatus = 'escalated';
          shouldResolve = false; // Stays in Next Steps
          break;
      }

      const response = await fetch(`/api/callbacks/${selectedCallId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          callback_status: callbackStatus,
          callback_agent: user.email,
          callback_notes: closeComment,
          callback_completed_at: shouldResolve ? new Date().toISOString() : null,
          ticket_status: ticketStatus,
          resolved: shouldResolve
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update ticket');
      }

      // Optimistically update the UI by removing the ticket from the list if resolved
      if (shouldResolve && reportData) {
        setReportData({
          ...reportData,
          actions: reportData.actions.filter(action => action.call_id !== selectedCallId),
          summary: {
            ...reportData.summary,
            totalActions: reportData.summary.totalActions - 1,
            pendingActions: reportData.summary.pendingActions - 1
          }
        });
      } else {
        // Refresh the data to show updated status
        await fetchReportData();
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating ticket:', err);
      setError('Failed to update ticket. Please try again.');
    } finally {
      setClosingTicket(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredActions = reportData?.actions
    .map(action => ({
      ...action,
      priorityScore: calculatePriorityScore(action)
    }))
    .filter(action => {
      const searchLower = searchTerm.toLowerCase();
      return (
        action.customer_name?.toLowerCase().includes(searchLower) ||
        action.customer_phone?.toLowerCase().includes(searchLower) ||
        action.issue_category?.toLowerCase().includes(searchLower) ||
        action.action_required?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => (b.priorityScore || 0) - (a.priorityScore || 0)) // Sort by priority score
  || [];

  const paginatedActions = filteredActions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionTypeIcon = (action: ActionItem) => {
    if (action.callback_requested) return <PhoneCallbackIcon fontSize="small" />;
    if (action.human_transfer_requested) return <PersonIcon fontSize="small" />;
    if (action.escalation_requested) return <WarningIcon fontSize="small" />;
    if (action.under_30_seconds) return <SpeedIcon fontSize="small" />;
    return <AssignmentIcon fontSize="small" />;
  };

  const getActionTypeLabel = (action: ActionItem) => {
    if (action.callback_requested) return 'Callback';
    if (action.human_transfer_requested) return 'Human Transfer';
    if (action.escalation_requested) return 'Escalation';
    if (action.under_30_seconds) return 'Quick Hang-up';
    return 'Follow Up';
  };

  // Get meaningful action description
  const getActionDescription = (action: ActionItem): string => {
    // Priority 1: Use issue_details if available
    if (action.issue_details && action.issue_details.trim() && action.issue_details !== 'Review required') {
      return action.issue_details;
    }
    
    // Priority 2: Build from next_steps array
    if (action.next_steps && action.next_steps.length > 0) {
      return action.next_steps[0]; // Use first next step
    }
    
    // Priority 3: Build from flags
    if (action.callback_requested) return 'Customer requested callback';
    if (action.human_transfer_requested) return 'Requested human agent assistance';
    if (action.escalation_requested) return 'Issue needs escalation';
    if (action.under_30_seconds) return 'Call disconnected - follow up needed';
    
    // Priority 4: Use troubleshooting context if available
    if (action.troubleshooting_steps && action.troubleshooting_steps.trim()) {
      return `Continue troubleshooting: ${action.troubleshooting_steps.substring(0, 100)}...`;
    }
    
    // Fallback: Generic by category
    return `Follow up on ${action.issue_category || 'support'} issue`;
  };

  if (loading && !reportData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !reportData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchReportData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Contextual Alerts */}
      {overdueCallbacks.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <AlertTitle>⏰ {overdueCallbacks.length} High-Priority Callback{overdueCallbacks.length > 1 ? 's' : ''} Overdue</AlertTitle>
          <Typography variant="body2">
            {overdueCallbacks.slice(0, 3).map((action, idx) => {
              const hoursSince = Math.floor((Date.now() - new Date(action.created_at).getTime()) / (1000 * 60 * 60));
              return (
                <span key={action.call_id}>
                  <strong>{action.customer_name || 'Unknown'}</strong> ({Math.floor(hoursSince / 24)} days ago)
                  {idx < Math.min(2, overdueCallbacks.length - 1) ? ', ' : ''}
                </span>
              );
            })}
            {overdueCallbacks.length > 3 && ` and ${overdueCallbacks.length - 3} more`}
          </Typography>
          <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setFilterPriority('High')}>
            View All Overdue
          </Button>
        </Alert>
      )}

      {/* Info Alert with keyboard shortcuts */}
      <Alert severity="info" sx={{ mb: 2 }} icon={<UpdateIcon />}>
        <Typography variant="body2">
          <strong>Last updated:</strong> {formatTimeAgo(lastFetchTime)} • 
          <strong> Shortcuts:</strong> ⌘K (Search) • ⌘R (Refresh) • ⌘N (Completed Tickets)
        </Typography>
      </Alert>

      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#080707' }}>
            Next Steps Report
          </Typography>
          <Typography variant="body2" sx={{ color: '#706E6B' }}>
            Action items requiring follow-up and attention
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/completed-callbacks')}
          >
            View Completed Tickets
          </Button>
          <Tooltip title="Refresh data (⌘R)">
            <IconButton onClick={fetchReportData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportNextStepsToCSV(reportData?.actions || [])}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => exportNextStepsToExcel(filteredActions)}
          >
            Export Excel
          </Button>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #546E7A 0%, #455A64 100%)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {reportData?.summary.totalActions || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Total Actions</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #5C6BC0 0%, #3F51B5 100%)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccessTimeIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {reportData?.summary.pendingActions || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Pending</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <PhoneCallbackIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {reportData?.summary.callbackRequests || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Callbacks</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #7E57C2 0%, #673AB7 100%)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <WarningIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {reportData?.summary.highPriorityActions || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>High Priority</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">Human Transfers</Typography>
                  <Typography variant="h6">{reportData?.summary.humanTransferRequests || 0}</Typography>
                </Box>
                <PersonIcon color="primary" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">Escalations</Typography>
                  <Typography variant="h6">{reportData?.summary.escalationRequests || 0}</Typography>
                </Box>
                <TrendingUpIcon color="error" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">Under 30 Seconds</Typography>
                  <Typography variant="h6">{reportData?.summary.under30Seconds || 0}</Typography>
                </Box>
                <SpeedIcon color="warning" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search actions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={filterPeriod}
                  label="Period"
                  onChange={(e) => setFilterPeriod(e.target.value)}
                >
                  <MenuItem value="24h">Last 24 Hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  label="Priority"
                  onChange={(e) => setFilterPriority(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Internet">Internet</MenuItem>
                  <MenuItem value="Phone">Phone</MenuItem>
                  <MenuItem value="Billing">Billing</MenuItem>
                  <MenuItem value="General Support">General Support</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={fetchReportData}
                disabled={loading}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTickets.length === paginatedActions.length && paginatedActions.length > 0}
                      indeterminate={selectedTickets.length > 0 && selectedTickets.length < paginatedActions.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTickets(paginatedActions.map(a => a.call_id));
                        } else {
                          setSelectedTickets([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Action Required</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority Score</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedActions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No action items found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedActions.map((action) => (
                    <TableRow 
                      key={action.call_id}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: getRowBackgroundColor(action.priorityScore || 0)
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedTickets.includes(action.call_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTickets([...selectedTickets, action.call_id]);
                            } else {
                              setSelectedTickets(selectedTickets.filter(id => id !== action.call_id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {action.customer_name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.customer_phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Typography variant="body2">
                          {action.created_at ? new Date(action.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.created_at ? new Date(action.created_at).toLocaleTimeString() : ''}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Chip 
                          label={action.issue_category || 'General'} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {getActionDescription(action)}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Chip 
                          icon={getActionTypeIcon(action)}
                          label={getActionTypeLabel(action)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Tooltip title={`Auto-calculated priority: ${action.priorityScore}/100`}>
                          <Chip 
                            label={action.priorityScore}
                            size="small"
                            color={getPriorityScoreColor(action.priorityScore || 0)}
                            sx={{ fontWeight: 700, minWidth: 50 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Chip 
                          icon={action.resolved ? <CheckCircleIcon fontSize="small" /> : <AccessTimeIcon fontSize="small" />}
                          label={action.resolved ? 'Completed' : 'Pending'}
                          size="small"
                          color={action.resolved ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/calls/${action.call_id}`)}>
                        <Chip 
                          label={action.channel_type || 'VOICE'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {!action.resolved && (
                          <Tooltip title="Close Ticket">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCloseDialog(action.call_id);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredActions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Bulk Action Bar (fixed at bottom when items selected) */}
      {selectedTickets.length > 0 && (
        <Paper 
          elevation={8}
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            p: 2, 
            zIndex: 1000,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Badge badgeContent={selectedTickets.length} color="error">
              <AssignmentIcon sx={{ color: 'white' }} />
            </Badge>
            <Typography fontWeight={600}>
              {selectedTickets.length} ticket{selectedTickets.length > 1 ? 's' : ''} selected
            </Typography>
            <Button 
              variant="contained" 
              color="inherit"
              startIcon={<PersonIcon />}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              Assign to Agent
            </Button>
            <Button 
              variant="contained" 
              color="inherit"
              startIcon={<EmailIcon />}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              Send Email
            </Button>
            <Button 
              variant="contained" 
              color="success"
              startIcon={<CheckCircleIcon />}
            >
              Mark Completed
            </Button>
            <Button 
              variant="text" 
              startIcon={<CloseIcon />}
              onClick={() => setSelectedTickets([])}
              sx={{ color: 'white' }}
            >
              Clear Selection
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Close Ticket Dialog */}
      <Dialog 
        open={closeDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Update Ticket</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Logged in as: <strong>{user?.email || 'Unknown'}</strong>
              </Typography>
            </Box>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                Status
              </FormLabel>
              <RadioGroup
                value={ticketStatus}
                onChange={(e) => setTicketStatus(e.target.value)}
              >
                <FormControlLabel 
                  value="callback_done" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Callback Done</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Customer contacted and issue resolved. Ticket will be closed.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="email_sent" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Email Sent</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Email sent to customer. Ticket will be closed.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="needs_followup" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Needs Follow-up</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Additional follow-up required. Ticket stays in Next Steps.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="escalated" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Escalated</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Escalated to manager/specialist. Ticket stays in Next Steps.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Notes"
              placeholder="Describe what you did or what needs to happen next..."
              value={closeComment}
              onChange={(e) => setCloseComment(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              helperText="Document your actions or next steps required"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={closingTicket}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloseTicket}
            variant="contained"
            color={ticketStatus === 'callback_done' || ticketStatus === 'email_sent' ? 'success' : 'primary'}
            disabled={closingTicket || !closeComment.trim()}
            startIcon={closingTicket ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {closingTicket ? 'Updating...' : 'Update Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NextStepsReport;
